import React, { useState, useEffect, useMemo } from 'react';
import { CreditCard, Wallet, PiggyBank, Calendar, Percent, TrendingUp } from 'lucide-react';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ReferenceLine,
} from 'recharts';
import { colors, API, fmt, fmtNum, authHeaders } from '../utils/theme';
import { StatCard, Th, Td, TrHover, LoadingSpinner, SearchableSelect } from '../components/UI';
import useSort from '../hooks/useSort';
import useIsMobile from '../hooks/useIsMobile';

// Cor do percentual de utilização
function pctColor(p) {
  if (p == null) return colors.textMuted;
  if (p >= 90) return colors.danger;
  if (p >= 70) return colors.warning;
  return colors.success;
}

// Pequena barra horizontal mostrando % de uso
function PctBar({ pct }) {
  const p = Math.max(0, Math.min(100, Number(pct) || 0));
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ width: 60, height: 6, background: colors.bg, borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${p}%`, height: '100%', background: pctColor(pct), borderRadius: 3 }} />
      </div>
      <span style={{ fontSize: 11, color: pctColor(pct), fontWeight: 600, minWidth: 38 }}>
        {p.toFixed(0)}%
      </span>
    </div>
  );
}

function InvestidoresTable({ rows }) {
  const { sorted, sortKey, sortDir, toggleSort } = useSort(rows, 'limite_utilizado', 'desc');
  const sortState = { sortKey, sortDir };
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr>
          <Th sortKey="nome" sortState={sortState} onSort={toggleSort}>Investidor</Th>
          <Th sortKey="cartoes_ativos" sortState={sortState} onSort={toggleSort} align="center">Ativos</Th>
          <Th sortKey="limite_total" sortState={sortState} onSort={toggleSort}>Limite</Th>
          <Th sortKey="limite_utilizado" sortState={sortState} onSort={toggleSort}>Utilizado</Th>
          <Th sortKey="limite_disponivel" sortState={sortState} onSort={toggleSort}>Disponível</Th>
          <Th sortKey="pct_utilizacao" sortState={sortState} onSort={toggleSort}>% Uso</Th>
          <Th sortKey="vencendo_7d" sortState={sortState} onSort={toggleSort} align="center">Venc. 7d</Th>
        </tr>
      </thead>
      <tbody>
        {sorted.map(t => (
          <TrHover key={t.id}>
            <Td style={{ fontWeight: 600 }}>{t.nome}</Td>
            <Td style={{ textAlign: 'center' }}>{t.cartoes_ativos}</Td>
            <Td>{fmt(t.limite_total)}</Td>
            <Td style={{ color: colors.danger, fontWeight: 600 }}>{fmt(t.limite_utilizado)}</Td>
            <Td style={{ color: colors.success }}>{fmt(t.limite_disponivel)}</Td>
            <Td><PctBar pct={t.pct_utilizacao} /></Td>
            <Td style={{ textAlign: 'center', color: t.vencendo_7d > 0 ? colors.warning : colors.textDim, fontWeight: 600 }}>
              {t.vencendo_7d}
            </Td>
          </TrHover>
        ))}
      </tbody>
    </table>
  );
}

function CartoesTable({ rows }) {
  const { sorted, sortKey, sortDir, toggleSort } = useSort(rows, 'pct_utilizacao', 'desc');
  const sortState = { sortKey, sortDir };
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr>
          <Th sortKey="nome_cartao" sortState={sortState} onSort={toggleSort}>Cartão</Th>
          <Th sortKey="titular_nome" sortState={sortState} onSort={toggleSort}>Titular</Th>
          <Th sortKey="limite_total" sortState={sortState} onSort={toggleSort}>Limite</Th>
          <Th sortKey="limite_utilizado" sortState={sortState} onSort={toggleSort}>Utilizado</Th>
          <Th sortKey="limite_disponivel" sortState={sortState} onSort={toggleSort}>Disponível</Th>
          <Th sortKey="pct_utilizacao" sortState={sortState} onSort={toggleSort}>% Uso</Th>
          <Th sortKey="dias_para_vencer" sortState={sortState} onSort={toggleSort} align="center">Vence em</Th>
        </tr>
      </thead>
      <tbody>
        {sorted.map(c => {
          const d = c.dias_para_vencer;
          const vencColor = d == null ? colors.textDim
            : d <= 0 ? colors.danger
            : d <= 7 ? colors.warning
            : colors.text;
          return (
            <TrHover key={c.id}>
              <Td>
                <div style={{ fontWeight: 600 }}>{c.nome_cartao}</div>
                <div style={{ fontSize: 11, color: colors.textMuted, display: 'flex', gap: 6, alignItems: 'center' }}>
                  {c.banco_nome}
                  {/* Open Finance conectado (02/11/2026): limite, utilizado e disponível do banco. */}
                  {c.limite_fonte === 'open_finance' && (
                    <span title={`Limite e disponível informados pelo banco (Open Finance)${c.limite_atualizado_em ? ` em ${new Date(c.limite_atualizado_em).toLocaleString('pt-BR')}` : ''}`}
                      style={{ fontSize: 9, padding: '1px 6px', borderRadius: 8, fontWeight: 700,
                        background: `${colors.success}25`, color: colors.success }}>OPEN FINANCE</span>
                  )}
                </div>
              </Td>
              <Td style={{ fontSize: 12 }}>{c.titular_nome}</Td>
              <Td style={{ whiteSpace: 'nowrap' }}>
                {fmt(c.limite_total)}
                {c.limite_fonte === 'open_finance' && (
                  <div style={{ fontSize: 10, color: colors.textMuted }} title="Limite do cadastro">cadastro {fmt(c.limite_cadastro)}</div>
                )}
              </Td>
              <Td style={{ color: colors.danger, fontWeight: 600 }}>{fmt(c.limite_utilizado)}</Td>
              <Td style={{ color: colors.success }}>{fmt(c.limite_disponivel)}</Td>
              <Td><PctBar pct={c.pct_utilizacao} /></Td>
              <Td style={{ textAlign: 'center', color: vencColor, fontWeight: d != null && d <= 7 ? 700 : 400 }}>
                {d == null ? '—' : d === 0 ? 'Hoje' : `${d}d`}
              </Td>
            </TrHover>
          );
        })}
      </tbody>
    </table>
  );
}

// ============================================================================
// Gráfico Fluxo de Pagamentos: Projetado (XMiles/Manager) x Real (PDF de fatura)
// ============================================================================
function FluxoPagamentosChart({ titularId }) {
  const [fluxo, setFluxo] = useState(null);
  const [janelaDias, setJanelaDias] = useState(60); // 30 | 60 | 90
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const passado = Math.floor(janelaDias / 4);
    const futuro = janelaDias - passado;
    const qs = new URLSearchParams({ dias_passado: passado, dias_futuro: futuro });
    if (titularId) qs.set('titular_id', titularId);
    fetch(`${API}/dashboard/fluxo-pagamentos?${qs}`)
      .then(r => r.json()).then(setFluxo).catch(console.error)
      .finally(() => setLoading(false));
  }, [janelaDias, titularId]);

  const { dados, hojeStr } = useMemo(() => {
    if (!fluxo?.serie) return { dados: [], hojeStr: null };
    return {
      dados: fluxo.serie.map(s => ({
        ...s,
        // label curto pro eixo X (DD/MM)
        label: s.data.slice(8, 10) + '/' + s.data.slice(5, 7),
      })),
      hojeStr: fluxo.janela?.hoje,
    };
  }, [fluxo]);

  const hojeLabel = hojeStr
    ? hojeStr.slice(8, 10) + '/' + hojeStr.slice(5, 7)
    : null;

  const formatTooltipValor = (v) => fmt(Number(v) || 0);

  return (
    <div style={{ background: colors.card, borderRadius: 12, padding: 20, border: `1px solid ${colors.border}`, marginBottom: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Fluxo de Pagamentos Diário</h3>
          <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 4 }}>
            <span style={{ color: colors.info }}>Projetado</span> (lançamentos XMiles do Manager) vs <span style={{ color: colors.success }}>Real</span> (após upload de fatura)
          </div>
        </div>
        <div style={{ display: 'flex', gap: 4, background: colors.bg, borderRadius: 8, padding: 3, border: `1px solid ${colors.border}` }}>
          {[30, 60, 90].map(d => (
            <button key={d} onClick={() => setJanelaDias(d)} style={{
              padding: '5px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
              fontSize: 12, fontWeight: 600,
              background: janelaDias === d ? colors.primary : 'transparent',
              color: janelaDias === d ? '#fff' : colors.textMuted, transition: 'all 0.15s',
            }}>{d}d</button>
          ))}
        </div>
      </div>

      {/* Totais da janela */}
      {fluxo?.totais && (
        <div style={{ display: 'flex', gap: 24, marginBottom: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 11, color: colors.textMuted, textTransform: 'uppercase' }}>Projetado</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: colors.info }}>{fmt(fluxo.totais.projetado)}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: colors.textMuted, textTransform: 'uppercase' }}>Real</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: colors.success }}>{fmt(fluxo.totais.real)}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: colors.textMuted, textTransform: 'uppercase' }}>Δ Real − Projetado</div>
            <div style={{
              fontSize: 18, fontWeight: 700,
              color: fluxo.totais.delta >= 0 ? colors.danger : colors.success,
            }}>
              {fluxo.totais.delta >= 0 ? '+' : ''}{fmt(fluxo.totais.delta)}
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: colors.textMuted }}>Carregando gráfico...</div>
      ) : (
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={dados} margin={{ top: 10, right: 16, left: 8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={colors.border} vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: colors.textMuted }} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 11, fill: colors.textMuted }} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
            <Tooltip
              contentStyle={{ background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 8, fontSize: 12 }}
              labelStyle={{ color: colors.textMuted }}
              formatter={formatTooltipValor}
              labelFormatter={(label, payload) => {
                const item = payload?.[0]?.payload;
                return item?.data || label;
              }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {hojeLabel && (
              <ReferenceLine x={hojeLabel} stroke={colors.warning} strokeDasharray="3 3"
                label={{ value: 'hoje', position: 'top', fill: colors.warning, fontSize: 10 }} />
            )}
            <Bar dataKey="projetado" name="Projetado (Manager)" fill={colors.info} opacity={0.6} radius={[3, 3, 0, 0]} />
            <Line type="monotone" dataKey="real" name="Real (Fatura PDF)" stroke={colors.success} strokeWidth={2} dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Filtro por titular (02/11/2026): KPIs, gráfico e tabelas passam a ser os
  // do titular escolhido. As opções são os titulares do escopo do usuário.
  const [titularId, setTitularId] = useState('');
  const [titulares, setTitulares] = useState([]);
  const mobile = useIsMobile();

  useEffect(() => {
    fetch(`${API}/titulares?com_cartao_ativo=true`, { headers: authHeaders() })
      .then(r => r.json()).then(d => setTitulares(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/dashboard${titularId ? `?titular_id=${titularId}` : ''}`)
      .then(r => r.json()).then(setData).catch(console.error).finally(() => setLoading(false));
  }, [titularId]);

  if (loading && !data) return <LoadingSpinner />;
  if (!data) return <div style={{ padding: 40 }}>Erro ao carregar dados. Verifique se o servidor está rodando.</div>;

  const r = data.resumo || {};

  return (
    <div>
      {/* Removidos a pedido: o toggle "Incluir SCP" (escondia titular de
          verdade por padrão) e o alerta de titulares-placeholder, que listava
          CONTA e AJUSTE — informação de manutenção que ainda por cima vazava
          pra investidor, já que esses titulares não são da operação dele. */}

      {titulares.length > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          <SearchableSelect value={titularId} onChange={setTitularId} placeholder="Todos os titulares"
            style={{ width: mobile ? '100%' : 320 }}
            options={titulares.map(t => ({ value: String(t.id), label: t.nome }))} />
          {titularId && (
            <button onClick={() => setTitularId('')}
              style={{ background: 'none', border: 'none', color: colors.primary, cursor: 'pointer', fontSize: 13 }}>
              Limpar filtro
            </button>
          )}
          {loading && <span style={{ fontSize: 12, color: colors.textMuted }}>Atualizando…</span>}
        </div>
      )}

      {/* KPIs */}
      {/* Celular: KPIs de dois em dois e as tabelas uma embaixo da outra. */}
      <div style={{ display: 'grid', gridTemplateColumns: mobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(6, 1fr)', gap: mobile ? 10 : 16, marginBottom: mobile ? 16 : 24, opacity: loading ? 0.6 : 1 }}>
        <StatCard label="Cartões Ativos" value={fmtNum(r.cartoes_ativos)} icon={CreditCard} />
        <StatCard label="Limite Total"   value={fmt(r.limite_total)}      icon={Wallet} />
        <StatCard label="Utilizado"      value={fmt(r.limite_utilizado)}  color={colors.danger}  icon={TrendingUp} />
        <StatCard label="Disponível"     value={fmt(r.limite_disponivel)} color={colors.success} icon={PiggyBank} />
        <StatCard label="Vencendo 7d"    value={fmtNum(r.vencendo_7d)}    color={r.vencendo_7d > 0 ? colors.warning : colors.success} icon={Calendar} />
        <StatCard label="% Utilização"   value={`${Number(r.pct_utilizacao || 0).toFixed(1)}%`} color={pctColor(r.pct_utilizacao)} icon={Percent} />
      </div>

      {/* Gráfico de Fluxo Projetado vs Real */}
      <FluxoPagamentosChart titularId={titularId} />

      <div style={{ display: 'grid', gridTemplateColumns: mobile ? 'minmax(0, 1fr)' : '1fr 1fr', gap: mobile ? 14 : 20 }}>
        {/* Esquerda: Investidores */}
        <div style={{ background: colors.card, borderRadius: 12, padding: 20, border: `1px solid ${colors.border}` }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginTop: 0, marginBottom: 4 }}>Por Investidor</h3>
          <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 12 }}>
            {(data.investidores || []).length} investidor(es) com cartões ativos
          </div>
          <div style={{ maxHeight: 600, overflow: 'auto' }}>
            <InvestidoresTable rows={data.investidores || []} />
          </div>
        </div>

        {/* Direita: Cartões */}
        <div style={{ background: colors.card, borderRadius: 12, padding: 20, border: `1px solid ${colors.border}` }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginTop: 0, marginBottom: 4 }}>Por Cartão</h3>
          <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 12 }}>
            {(data.cartoes || []).length} cartão(ões) ativo(s), ordenado por % de uso
          </div>
          <div style={{ maxHeight: 600, overflow: 'auto' }}>
            <CartoesTable rows={data.cartoes || []} />
          </div>
        </div>
      </div>
    </div>
  );
}
